'use client';

import { useRef, useState, type FormEvent } from 'react';
import { Bot } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { DcyfrButton } from '@/components/ui/dcyfr-button';
import { DcyfrTextarea } from '@/components/ui/dcyfr-textarea';
import { TYPOGRAPHY, SPACING, BORDERS } from '@/lib/design-tokens';
import { cn } from '@/lib/utils';

type ChatMessage = { role: 'user' | 'assistant'; content: string };

export type SiteAgentChatProps = {
  personaId: string;
  displayName: string;
  disclosure: string;
  greeting: string;
  suggestions: string[];
};

const MAX_CHARS = 1000;

/**
 * Site Agent Chat
 *
 * A small chat panel for the public site agents. The transcript lives only in
 * this component's state; nothing is stored. The disclosure line is always
 * visible, never behind a toggle.
 */
export function SiteAgentChat({
  personaId,
  displayName,
  disclosure,
  greeting,
  suggestions,
}: SiteAgentChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  async function send(text: string) {
    const content = text.trim();
    if (!content || pending) return;
    const next: ChatMessage[] = [...messages, { role: 'user', content }];
    setMessages(next);
    setDraft('');
    setError(null);
    setPending(true);
    try {
      const res = await fetch(`/api/agents/${personaId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next }),
      });
      const data = (await res.json().catch(() => ({}))) as { reply?: string; error?: string };
      if (!res.ok || !data.reply) {
        setError(data.error || `${displayName} couldn't answer. Try again in a minute.`);
        setMessages(messages);
        setDraft(content);
        return;
      }
      setMessages([...next, { role: 'assistant', content: data.reply }]);
    } catch {
      setError(`${displayName} couldn't be reached. Check your connection and try again.`);
      setMessages(messages);
      setDraft(content);
    } finally {
      setPending(false);
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: 'nearest' }));
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void send(draft);
  }

  return (
    <Card className={cn('p-4 sm:p-6', BORDERS.card)} aria-label={`Chat with ${displayName}`}>
      <div className={SPACING.content}>
        <div className="flex items-center gap-2.5">
          <Bot className="h-5 w-5 text-primary" aria-hidden="true" />
          <h3 className={TYPOGRAPHY.h3.standard}>Ask {displayName}</h3>
        </div>
        <p className={TYPOGRAPHY.metadata}>{disclosure}</p>

        <div className={SPACING.activity.contentGap} aria-live="polite">
          <p className="text-sm">{greeting}</p>
          {messages.map((m, i) => (
            <div
              key={i}
              className={cn(
                'rounded-md px-3 py-2 text-sm whitespace-pre-wrap',
                m.role === 'user' ? 'bg-muted ml-8' : 'bg-primary/5 mr-8'
              )}
            >
              <span className="sr-only">{m.role === 'user' ? 'You' : displayName}: </span>
              {m.content}
            </div>
          ))}
          {pending && <p className={TYPOGRAPHY.metadata}>{displayName} is thinking…</p>}
          <div ref={endRef} />
        </div>

        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <DcyfrButton
                key={s}
                type="button"
                variant="ghostly"
                size="sm"
                className="h-auto whitespace-normal border border-input py-1.5 text-left"
                onClick={() => void send(s)}
                disabled={pending}
              >
                {s}
              </DcyfrButton>
            ))}
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <form onSubmit={onSubmit} className={SPACING.compact}>
          <label htmlFor={`site-agent-${personaId}`} className="sr-only">
            Message {displayName}
          </label>
          <DcyfrTextarea
            id={`site-agent-${personaId}`}
            value={draft}
            maxLength={MAX_CHARS}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(draft);
              }
            }}
            placeholder="Ask a question"
            className="min-h-[80px]"
            disabled={pending}
          />
          <div className="flex items-center justify-between gap-3">
            <span className={TYPOGRAPHY.metadata}>
              This site doesn't keep conversations. Don't share anything private.
            </span>
            <DcyfrButton type="submit" disabled={pending || !draft.trim()}>
              Send
            </DcyfrButton>
          </div>
        </form>
      </div>
    </Card>
  );
}
